import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { formatAmount, formatMoney } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { ProductCard } from '../../ui/ProductCard'
import { useOnVisible } from '../../ui/useOnVisible'
import { useCart } from '../cart/CartProvider'
import { useCategories, useProducts } from './api'
import { FilterSheet } from './FilterSheet'
import { SearchEntry } from '../multisearch'
import { env } from '../../config/env'
import {
  type CatalogState,
  EMPTY_FILTERS,
  activePills,
  filterCount,
  readCatalog,
  sheetCount,
  toQuery,
  writeCatalog,
} from './model'

export function CatalogScreen() {
  const { t, i18n } = useTranslation(['catalog', 'common'])
  const navigate = useNavigate()
  const cart = useCart()
  const [params, setParams] = useSearchParams()

  // Отбор хранится в адресе (см. `readCatalog`), но поля ввода читают свою
  // копию: адрес обновляется переходом, и поле, привязанное к нему напрямую,
  // теряло бы курсор на каждой букве.
  const [state, setState] = useState(() => readCatalog(params))
  const written = useRef(params.toString())
  const { category, search, filters, sort } = state
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Адрес сменили снаружи — ссылка на категорию, шаг «назад» по истории.
  const query = params.toString()
  useEffect(() => {
    if (query === written.current) return
    written.current = query
    setState(readCatalog(new URLSearchParams(query)))
  }, [query])

  /** Новый отбор — в экран и в адрес. Смена категории — шаг истории, остальное заменяет запись. */
  const update = (patch: Partial<CatalogState>) => {
    const next = { ...state, ...patch }
    const address = writeCatalog(next)
    setState(next)
    written.current = address.toString()
    setParams(address, { replace: patch.category === undefined })
  }
  const setSearch = (value: string) => {
    update({ search: value })
  }
  const setFilters = (value: CatalogState['filters']) => {
    update({ filters: value })
  }

  const categories = useCategories()
  const products = useProducts(toQuery(state), i18n.language)
  const list = products.data?.items ?? []
  const loadMore = () => {
    void products.fetchNextPage()
  }
  // Следующая порция просится сама, когда низ списка подходит к экрану.
  const moreRef = useOnVisible(
    loadMore,
    products.hasNextPage && !products.isFetchingNextPage && !products.isFetchNextPageError,
  )
  const pills = activePills(filters, formatMoney, t)
  const active = filterCount(filters)
  const badge = sheetCount(filters, category, sort)

  const setCategory = (id: number) => {
    update({ category: id })
  }

  return (
    <section className="screen">
      <div className="cat-sticky">
        <div className="cs-top">
          <h1>{t('catalog:title')}</h1>
        </div>
        <div className="cs-row">
          {/* С мультипоиском поле открывает поиск на весь экран; без флага — как было. */}
          {env.features.multiSearch ? (
            <SearchEntry />
          ) : (
            <div className="search">
              <Icon name="i-search" size={19} />
              <input
                value={search}
                placeholder={t('catalog:searchShort')}
                aria-label={t('catalog:searchShort')}
                autoComplete="off"
                onChange={(event) => {
                  setSearch(event.target.value)
                }}
              />
            </div>
          )}
          <Press
            squish="icon"
            className="hicon cs-filter"
            aria-label={t('catalog:filters')}
            onPress={() => {
              setFiltersOpen(true)
            }}
          >
            <Icon name="i-filter" />
            {badge > 0 ? <span className="hbadge num">{badge}</span> : null}
          </Press>
        </div>
      </div>

      <div className="fbar">
        {pills.map((pill) => (
          <span key={pill.key} className="fpill">
            {pill.label}
            <button
              type="button"
              aria-label={`${t('catalog:reset')}: ${pill.label}`}
              onClick={() => {
                setFilters({ ...filters, [pill.key]: pill.reset })
              }}
            >
              ×
            </button>
          </span>
        ))}
        {active > 0 ? (
          <Press
            squish="chip"
            className="fbtn"
            onPress={() => {
              setFilters(EMPTY_FILTERS)
            }}
          >
            {t('catalog:reset')}
          </Press>
        ) : null}
      </div>

      {products.isPending ? (
        <Loading what={t('common:state.loading')} shape="products" count={6} />
      ) : null}
      {products.isError && !products.data ? (
        <Failed
          error={products.error}
          onRetry={() => {
            void products.refetch()
          }}
        />
      ) : null}

      {products.data ? (
        <>
          <div className="foundline">
            {products.data.capped
              ? t('catalog:foundCapped', { n: formatAmount(products.data.total) })
              : t('catalog:found', { count: products.data.total })}
          </div>

          {list.length === 0 ? (
            <div className="card empty">
              <Icon name="i-search" size={54} />
              <div className="t">{t('catalog:nothing')}</div>
              {t('catalog:nothingHint')}
            </div>
          ) : (
            <div className="grid">
              {list.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  quantity={cart.quantityOf(product.id)}
                  onOpen={(id) => {
                    void navigate(`/catalog/${id}`)
                  }}
                  onAdd={() => {
                    cart.changeQuantity(product.id, 1, product.maxQty)
                  }}
                  onChangeQuantity={(_product, delta) => {
                    cart.changeQuantity(product.id, delta, product.maxQty)
                  }}
                />
              ))}
            </div>
          )}

          <div ref={moreRef} className="cat-more">
            {products.isFetchingNextPage ? (
              <span role="status">{t('common:state.loading')}</span>
            ) : products.isFetchNextPageError ? (
              <Failed error={products.error} onRetry={loadMore} />
            ) : products.hasNextPage ? (
              <Press squish="chip" className="fbtn" onPress={loadMore}>
                {t('common:actions.showMore')}
              </Press>
            ) : null}
          </div>
        </>
      ) : null}

      <FilterSheet
        opened={filtersOpen}
        filters={filters}
        categories={categories.data ?? []}
        category={category}
        sort={sort}
        found={
          products.data?.capped
            ? `${formatAmount(products.data.total)}+`
            : String(products.data?.total ?? 0)
        }
        onChange={setFilters}
        onCategory={setCategory}
        onSort={(value) => {
          update({ sort: value })
        }}
        onReset={() => {
          setFilters(EMPTY_FILTERS)
        }}
        onClose={() => {
          setFiltersOpen(false)
        }}
      />
    </section>
  )
}
